// src/pages/about/Team.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTwitter, FaLinkedin, FaEnvelope, FaFacebook, FaInstagram, FaSpinner } from 'react-icons/fa';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Team = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch team members from backend
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/team');

        if (response.success && response.teamMembers) {
          setTeamMembers(response.teamMembers);
        } else {
          throw new Error('Failed to fetch team members');
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        toast.error('Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const getAvatarUrl = (member) => {
    // If member has a photo, use it
    if (member.photo) {
      return member.photo;
    }

    // Otherwise generate initials avatar
    const initials = member.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    // Color based on department
    const colorMap = {
      'Leadership': '22c55e',
      'Administration': '3b82f6',
      'Coaching': '8b5cf6',
      'Operations': 'f59e0b',
      'Marketing': 'ec4899',
      'Finance': '14b8a6',
      'Other': '6b7280'
    };

    const color = colorMap[member.department] || '22c55e';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${color}&color=ffffff&size=400&rounded=true&font-size=0.4`;
  };

  if (loading) {
    return (
      <div className="bg-white py-24 sm:py-32 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header Section */}
        <div className="mx-auto max-w-2xl sm:text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              Meet our team
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-600">
              Get to know the passionate professionals who are committed to driving innovation and excellence in youth development across Africa.
            </p>
          </motion.div>
        </div>

        {/* Team Members List */}
        {teamMembers.length === 0 ? (
          <div className="mt-20 text-center">
            <p className="text-gray-500">No team members available at the moment.</p>
          </div>
        ) : (
          <ul
            ref={ref}
            role="list"
            className="mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 sm:grid-cols-2 lg:max-w-4xl xl:max-w-none"
          >
            {teamMembers.map((member, index) => (
              <motion.li
                key={member._id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="flex flex-col items-start gap-x-10 gap-y-6 xl:flex-row"
              >
                {/* Profile Image */}
                <div className="flex-none">
                  <div className="relative aspect-square w-56 overflow-hidden rounded-full bg-gray-200">
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full rounded-full ring-1 ring-inset ring-gray-900/10"
                    ></span>
                    <img
                      width="400"
                      height="400"
                      className="h-full w-full object-cover"
                      src={getAvatarUrl(member)}
                      alt={`${member.name} - ${member.role}`}
                    />
                  </div>
                </div>

                {/* Member Info */}
                <div className="flex-auto">
                  <h3 className="text-2xl font-semibold leading-9 tracking-tight text-gray-900">
                    {member.name}
                  </h3>
                  <p className="text-lg leading-8 text-gray-600">
                    {member.role}
                  </p>

                  {/* Department Badge */}
                  {member.department && (
                    <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 mt-2">
                      {member.department}
                    </span>
                  )}

                  {/* Email Display */}
                  {member.email && (
                    <div className="mt-3 flex items-center gap-2">
                      <FaEnvelope className="h-4 w-4 text-gray-500" />
                      <a
                        href={`mailto:${member.email}`}
                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {member.email}
                      </a>
                    </div>
                  )}

                  {/* Bio */}
                  {member.bio && (
                    <p className="mt-4 text-base leading-7 text-gray-600">
                      {member.bio}
                    </p>
                  )}

                  {/* Expertise Tags */}
                  {member.expertise && member.expertise.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {member.expertise.map((skill, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Social Links */}
                  {member.socialMedia && (
                    <ul role="list" className="mt-6 flex gap-x-5">
                      {member.socialMedia.twitter && (
                        <li>
                          <a
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            href={member.socialMedia.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${member.name} Twitter`}
                          >
                            <span className="sr-only">Twitter</span>
                            <FaTwitter className="h-5 w-5" />
                          </a>
                        </li>
                      )}
                      {member.socialMedia.linkedin && (
                        <li>
                          <a
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            href={member.socialMedia.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${member.name} LinkedIn`}
                          >
                            <span className="sr-only">LinkedIn</span>
                            <FaLinkedin className="h-5 w-5" />
                          </a>
                        </li>
                      )}
                      {member.socialMedia.facebook && (
                        <li>
                          <a
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            href={member.socialMedia.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${member.name} Facebook`}
                          >
                            <span className="sr-only">Facebook</span>
                            <FaFacebook className="h-5 w-5" />
                          </a>
                        </li>
                      )}
                      {member.socialMedia.instagram && (
                        <li>
                          <a
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            href={member.socialMedia.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${member.name} Instagram`}
                          >
                            <span className="sr-only">Instagram</span>
                            <FaInstagram className="h-5 w-5" />
                          </a>
                        </li>
                      )}
                      {member.email && (
                        <li>
                          <a
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                            href={`mailto:${member.email}`}
                            aria-label={`Email ${member.name}`}
                          >
                            <span className="sr-only">Email</span>
                            <FaEnvelope className="h-5 w-5" />
                          </a>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        )}

        {/* Contact Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gray-50 rounded-3xl p-8"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            Additional Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-center">
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">General Inquiries</h4>
              <a
                href="mailto:info@africandebatechampions.org"
                className="text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-2"
              >
                <FaEnvelope className="h-4 w-4" />
                info@africandebatechampions.org
              </a>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Technical Support</h4>
              <a
                href="mailto:support@africandebatechampions.org"
                className="text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-2"
              >
                <FaEnvelope className="h-4 w-4" />
                support@africandebatechampions.org
              </a>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Eagles Club</h4>
              <a
                href="mailto:eaglesclub@africandebatechampions.org"
                className="text-blue-600 hover:text-blue-800 transition-colors flex items-center justify-center gap-2"
              >
                <FaEnvelope className="h-4 w-4" />
                eaglesclub@africandebatechampions.org
              </a>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-gray-50 rounded-3xl p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Work With Our Team?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Connect with our experienced team to discuss how we can help develop the next generation of African leaders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
              >
                Get In Touch
              </a>
              <a
                href="/programs"
                className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-orange-400 hover:bg-orange-50 transition-all duration-300"
              >
                View Our Programs
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Team;